/**
 * Advanced search — a composer for the search box, not a second search form.
 * (item 4)
 *
 * Every field here writes the same `title:` / `id:` / `text:` / `"phrase"` /
 * `-word` syntax a user can type directly, into the same box, which is then
 * sent as the same single `SearchableText` term. The consequences of that are
 * the reason for it:
 *
 *   • one URL describes any search, however it was built, so an advanced
 *     search is as shareable and bookmarkable as a simple one;
 *   • the backend has one query path to reason about, not two;
 *   • the panel TEACHES the syntax. The composed query is shown as you type,
 *     in the same form you could have typed — which is how someone stops
 *     needing the panel.
 *
 * Opening it decomposes whatever is already in the box, so it is a lens on the
 * current search rather than a blank slate that would throw it away. When a
 * query cannot be taken apart and put back together unchanged, helpers/
 * queryFields hands the whole thing back as plain text instead of showing a
 * decomposition that misrepresents it — see the round-trip note there.
 *
 * Nothing is applied until Apply. Typing in these fields never changes the
 * results underneath.
 */
import React from 'react';
import PropTypes from 'prop-types';

import { FIELD_PREFIXES } from '../../config/searchRegistry.generated';
import { parseQuery, composeQuery, EMPTY } from '../../helpers/queryFields';

const Field = ({ id, label, hint, placeholder, value, onChange, onEnter }) => (
  <div className="advanced-search__field">
    <label className="advanced-search__label" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      type="text"
      className="advanced-search__input"
      value={value}
      placeholder={placeholder}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter();
        }
      }}
    />
    {hint ? <span className="advanced-search__hint">{hint}</span> : null}
  </div>
);

const AdvancedPanel = ({ id, searchText, setSearchText, onTriggerSearch }) => {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  // Re-seed from the box whenever it changes while the panel is CLOSED, so
  // opening it always reflects the search actually on screen. Gated on `open`
  // because Apply writes the box, and re-seeding from our own output would
  // stamp the canonical spelling back over what the user is still editing.
  React.useEffect(() => {
    if (!open) setForm(parseQuery(searchText));
  }, [searchText, open]);

  const composed = composeQuery(form);
  const isEmpty = !composed;

  const setField = (alias, value) =>
    setForm((f) => ({ ...f, fields: { ...f.fields, [alias]: value } }));

  const apply = React.useCallback(() => {
    const q = composeQuery(form);
    setSearchText(q);
    onTriggerSearch(q);
  }, [form, setSearchText, onTriggerSearch]);

  const clear = () => {
    setForm({ ...EMPTY, fields: {} });
    setSearchText('');
    onTriggerSearch('');
  };

  const panelId = `${id}-advanced`;

  return (
    <div className="advanced-search">
      <button
        type="button"
        className="advanced-search__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide advanced search' : 'Advanced search'}
      </button>

      {open && (
        <div className="advanced-search__panel" id={panelId}>
          <div className="advanced-search__grid">
            <Field
              id={`${panelId}-rest`}
              label="All these words"
              hint="Matched anywhere — title, metadata, or the text inside the PDF."
              placeholder="nuclear facility safety"
              value={form.rest || ''}
              onChange={(v) => setForm((f) => ({ ...f, rest: v }))}
              onEnter={apply}
            />
            <Field
              id={`${panelId}-phrase`}
              label="This exact phrase"
              hint="Words in this order, together."
              placeholder="documented safety analysis"
              value={form.phrase || ''}
              onChange={(v) => setForm((f) => ({ ...f, phrase: v }))}
              onEnter={apply}
            />
            {FIELD_PREFIXES.map((f) => (
              <Field
                key={f.alias}
                id={`${panelId}-${f.alias}`}
                label={f.label}
                hint={f.hint}
                placeholder={f.placeholder}
                value={form.fields?.[f.alias] || ''}
                onChange={(v) => setField(f.alias, v)}
                onEnter={apply}
              />
            ))}
            <Field
              id={`${panelId}-exclude`}
              label="None of these words"
              hint="Documents containing any of these are left out."
              placeholder="draft handbook"
              value={form.exclude || ''}
              onChange={(v) => setForm((f) => ({ ...f, exclude: v }))}
              onEnter={apply}
            />
          </div>

          <div className="advanced-search__footer">
            <div className="advanced-search__preview">
              <span className="advanced-search__preview-label">
                Search box will read:
              </span>{' '}
              {isEmpty ? (
                <span className="advanced-search__preview-empty">
                  (everything)
                </span>
              ) : (
                <code className="advanced-search__preview-query">
                  {composed}
                </code>
              )}
            </div>
            <div className="advanced-search__actions">
              <button
                type="button"
                className="advanced-search__clear"
                onClick={clear}
              >
                Clear
              </button>
              <button
                type="button"
                className="advanced-search__apply"
                onClick={apply}
              >
                Search
              </button>
            </div>
          </div>

          <p className="advanced-search__note">
            You can type this directly in the search box:{' '}
            <code>title:welding</code>, <code>id:1104</code>,{' '}
            <code>text:&quot;criticality safety&quot;</code>,{' '}
            <code>-draft</code>.
          </p>
        </div>
      )}
    </div>
  );
};

AdvancedPanel.propTypes = {
  /** The search block's id; namespaces the input ids and the panel region. */
  id: PropTypes.string.isRequired,
  /** Current contents of the search box. */
  searchText: PropTypes.string,
  /** Write the search box (the block's controlled-input setter). */
  setSearchText: PropTypes.func.isRequired,
  /** Run the search. */
  onTriggerSearch: PropTypes.func.isRequired,
};

export default AdvancedPanel;
